import React from "react";
import { isNum } from "../../utils/format.js";
import { styles } from "../../styles/styles.js";

const WINDOW = 10;

// What "Last 10" is actually made of.
//
// `_get_recent_games` filters on league, game_time and happened and NOTHING
// else, so the recent window walks backwards until it has ten and crosses the
// summer break in every league. The blob carried no fact about when those games
// were played, and none about whether there were ten to find. Hydration writes
// both now -- {side}_history_depth and {side}_season_games_{n} -- and this is
// the only thing that reads them.
//
// Without it a newly promoted club is a row of em-dashes with no stated reason,
// which reads as a rendering failure rather than as an absence of history. The
// live case this was built against is Hull City, promoted into EPL 2026/27 with
// a depth of 0 against Manchester United's 10.
function phrase(team, depth, thisSeason) {
  if (!isNum(depth)) return null;
  if (depth === 0) return `${team}: no games in the last-${WINDOW} window.`;

  const games = `${depth} game${depth === 1 ? "" : "s"}`;
  const carried = isNum(thisSeason) ? `, ${thisSeason} of them this season` : "";
  const thin = depth < WINDOW;
  const crossesBreak = isNum(thisSeason) && thisSeason < depth;
  // Mid-season both are the full window and there is nothing to say.
  if (!thin && !crossesBreak) return null;
  return `${team}: ${games}${carried}.`;
}

const Note = ({ children }) => (
  <div style={{ ...styles.unknown, padding: "1vh 1vw", textAlign: "center" }}>
    {children}
  </div>
);

// The recent-form window, for the two teams of a fixture.
export const HistoryNote = ({ fixture }) => {
  const recent = fixture?.recent;
  if (!recent) return null;

  const lines = [
    phrase(fixture.home_team, recent.home_history_depth, recent[`home_season_games_${WINDOW}`]),
    phrase(fixture.away_team, recent.away_history_depth, recent[`away_season_games_${WINDOW}`]),
  ].filter(Boolean);

  if (lines.length === 0) return null;
  return <Note>{lines.map((line) => <div key={line}>{line}</div>)}</Note>;
};

// The head-to-head window. A first-ever meeting used to render as ten solid
// boxes and a fabricated record; it is a fact about the pairing, not about
// either team, so it gets its own sentence.
export const H2HHistoryNote = ({ game }) => {
  const meetings = game?.head_to_head?.[`h2h_games_sample_${WINDOW}`];
  if (!isNum(meetings) || meetings >= WINDOW) return null;
  return (
    <Note>
      {meetings === 0
        ? "First meeting: no head-to-head history."
        : `${meetings} previous meeting${meetings === 1 ? "" : "s"} on record.`}
    </Note>
  );
};

export default HistoryNote;
